import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { 
  Box, 
  Typography, 
  TextField, 
  Button, 
  Chip, 
  Paper, 
  TableContainer, 
  Table, 
  TableHead, 
  TableRow, 
  TableCell, 
  TableBody, 
  IconButton,
  InputAdornment,
  useTheme,
  CircularProgress,
  Alert,
  TablePagination,
  Tooltip
} from '@mui/material';
import { 
  Search as SearchIcon,
  Add as AddIcon,
  Schedule as ScheduleIcon,
  Edit as EditIcon,
  Delete as DeleteIcon
} from '@mui/icons-material';
import { whitelistService, chargersService } from '../services/api';
import { WhitelistEntry, RfidFormData } from '../types';
import AddRfidCardModal from '../components/AddRfidCardModal';

// RFID Access interface for display
interface RFIDAccess {
  id: string;
  rfid: string;
  customer: string;
  name: string;
  pricing: string;
  email: string;
  addedDate: string;
  lastUsed: string;
}

const ChargerAccessControl: React.FC = () => {
  const theme = useTheme();
  const { orgId, unitId } = useParams<{ orgId: string; unitId: string }>();
  const [searchTerm, setSearchTerm] = useState('');
  const [whitelistData, setWhitelistData] = useState<RFIDAccess[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [isRfidDisabled, setIsRfidDisabled] = useState(false);

  // Fetch charger data to check if RFID should be disabled
  useEffect(() => {
    const checkRfidAvailability = async () => {
      if (!orgId || !unitId) {
        return;
      }

      try {
        const response = await chargersService.getCharger(orgId, unitId);
        
        if (response.success && response.data && response.data.units && response.data.units.length > 0) {
          // Find the charger with the matching unit_id
          const charger = response.data.units.find((unit: any) => unit.unit_id === unitId);
          
          if (charger && charger.unit_name) {
            const unitName = charger.unit_name;
            // Check if unit_name starts with "KE", "HB", "BC", or "BP"
            const isRestricted = unitName.startsWith('KE') || 
                                unitName.startsWith('HB') || 
                                unitName.startsWith('BC') || 
                                unitName.startsWith('BP');
            
            setIsRfidDisabled(isRestricted);
          }
        }
      } catch (err) {
        console.error('Failed to check RFID availability:', err);
        // On error, don't disable the button (fail open)
      }
    };

    checkRfidAvailability();
  }, [orgId, unitId]);

  // Fetch whitelist data from API
  useEffect(() => {
    const fetchWhitelistData = async () => {
      if (!orgId || !unitId) {
        setError('Missing organization ID or unit ID');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);
        
        console.log('Fetching whitelist data for orgId:', orgId, 'unitId:', unitId);
        const response = await whitelistService.getWhitelist(orgId, unitId);
        
        if (response.success && response.data) {
          // Transform API data to component format
          const transformedData: RFIDAccess[] = response.data.entries.map((entry: WhitelistEntry, index: number) => ({
            id: entry.rfid, // Use RFID as unique identifier
            rfid: entry.rfid,
            customer: entry.customer || '', // Use customer field if available, otherwise empty string
            name: entry.user_name,
            pricing: 'N/A', // API doesn't provide pricing info
            email: entry.email,
            addedDate: new Date(entry.created_at).toLocaleDateString(),
            lastUsed: new Date(entry.updated_at).toLocaleString(),
          }));
          
          console.log('Whitelist data loaded:', transformedData);
          setWhitelistData(transformedData);
        } else {
          console.error('No whitelist data found');
          setWhitelistData([]);
        }
      } catch (err) {
        console.error('Failed to load whitelist data:', err);
        setError(err instanceof Error ? err.message : 'Failed to load whitelist data');
        setWhitelistData([]);
      } finally {
        setLoading(false);
      }
    };

    fetchWhitelistData();
  }, [orgId, unitId]);

  const filteredData = whitelistData.filter(item =>
    item.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    item.rfid.toLowerCase().includes(searchTerm.toLowerCase()) ||
    item.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
    item.customer.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleAddRfidCard = async (rfidData: RfidFormData) => {
    if (!orgId || !unitId) {
      setError('Missing organization ID or unit ID');
      return;
    }

    try {
      setIsSubmitting(true);
      setError(null);
      
      console.log('Adding RFID card:', rfidData);
      const response = await whitelistService.addRfidCard(orgId, unitId, rfidData);
      
      if (response.success) {
        console.log('RFID card added successfully');
        // Refresh the whitelist data
        const updatedResponse = await whitelistService.getWhitelist(orgId, unitId);
        if (updatedResponse.success && updatedResponse.data) {
          const transformedData: RFIDAccess[] = updatedResponse.data.entries.map((entry: WhitelistEntry, index: number) => ({
            id: entry.rfid,
            rfid: entry.rfid,
            customer: entry.customer || '', // Use customer field if available, otherwise empty string
            name: entry.user_name,
            pricing: 'N/A',
            email: entry.email,
            addedDate: new Date(entry.created_at).toLocaleDateString(),
            lastUsed: new Date(entry.updated_at).toLocaleString(),
          }));
          setWhitelistData(transformedData);
        }
        setIsModalOpen(false);
      } else {
        setError('Failed to add RFID card');
      }
    } catch (err) {
      console.error('Failed to add RFID card:', err);
      setError(err instanceof Error ? err.message : 'Failed to add RFID card');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleOpenModal = () => {
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Show loading state
  if (loading) {
    return (
      <Box sx={{ flexGrow: 1, backgroundColor: '#FAFAFA', p: 3, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress />
      </Box>
    );
  }

  // Show error state
  if (error) {
    return (
      <Box sx={{ flexGrow: 1, backgroundColor: '#FAFAFA', p: 3 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ flexGrow: 1, backgroundColor: '#FAFAFA', p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 600, color: theme.palette.text.primary }}>
          RFID Access
        </Typography>
        <Tooltip title={isRfidDisabled ? "RFID administration is not yet available for this charger" : ""}>
          <span>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleOpenModal}
              disabled={isRfidDisabled}
              sx={{
                backgroundColor: '#337A5C',
                '&:hover': {
                  backgroundColor: '#2a6350',
                },
                '&:disabled': {
                  backgroundColor: theme.palette.action.disabledBackground,
                  color: theme.palette.action.disabled,
                },
              }}
            >
              ADD RFID CARD
            </Button>
          </span>
        </Tooltip>
      </Box>
      <Typography variant="body1" sx={{ color: theme.palette.text.secondary, mb: 4 }}>
        This is the RFID Access page. Here you can manage user permissions and access rights.
      </Typography>

      {/* Search Section */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'center', mb: 3 }}>
        <TextField
          placeholder="Search by name, RFID, email, or company..."
          value={searchTerm}
          onChange={(e) => {
            setSearchTerm(e.target.value);
            setPage(0); // Reset to first page when searching
          }}
          size="small"
          sx={{ 
            width: 400,
            '& .MuiOutlinedInput-root': {
              height: '40px',
              borderRadius: '4px',
            },
            '& .MuiInputBase-input': {
              height: '40px',
              padding: '0 14px',
            },
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: theme.palette.text.secondary }} />
              </InputAdornment>
            ),
          }}
        />
      </Box>

      {/* RFID Access Table */}
      <TableContainer component={Paper} sx={{ boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
        <Table>
          <TableHead>
            <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
              <TableCell sx={{ fontWeight: 600, color: theme.palette.text.primary }}>RFID</TableCell>
              <TableCell sx={{ fontWeight: 600, color: theme.palette.text.primary }}>Company</TableCell>
              <TableCell sx={{ fontWeight: 600, color: theme.palette.text.primary }}>Name</TableCell>
              <TableCell sx={{ fontWeight: 600, color: theme.palette.text.primary }}>Pricing</TableCell>
              <TableCell sx={{ fontWeight: 600, color: theme.palette.text.primary }}>Email</TableCell>
              <TableCell sx={{ fontWeight: 600, color: theme.palette.text.primary }}>Added Date</TableCell>
              <TableCell sx={{ fontWeight: 600, color: theme.palette.text.primary }}>Last Used</TableCell>
              <TableCell sx={{ fontWeight: 600, color: theme.palette.text.primary }}> </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredData.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} sx={{ textAlign: 'center', py: 4 }}>
                  <Typography variant="body2" color="text.secondary">
                    No whitelist entries available
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              filteredData
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((row) => (
                <TableRow key={row.id} sx={{ '&:hover': { backgroundColor: '#f8f9fa' } }}>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontWeight: 500, color: theme.palette.text.primary }}>
                      {row.rfid}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontWeight: 500, color: theme.palette.text.primary }}>
                      {row.customer}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: theme.palette.text.primary }}>
                      {row.name}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={row.pricing}
                      size="small"
                      sx={{
                        backgroundColor: '#4CAF50',
                        color: 'white',
                        fontWeight: 600,
                        fontSize: '0.75rem'
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
                      {row.email}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
                      {row.addedDate}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <ScheduleIcon sx={{ fontSize: 16, color: theme.palette.text.secondary }} />
                      <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
                        {row.lastUsed}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <IconButton size="small" sx={{ color: '#1976D2' }}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton size="small" sx={{ color: '#F44336' }}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pagination */}
      <TablePagination
        rowsPerPageOptions={[5, 10, 25]}
        component="div"
        count={filteredData.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        sx={{ 
          borderTop: '1px solid rgba(0,0,0,0.12)',
          '& .MuiTablePagination-toolbar': {
            paddingLeft: 0,
            fontFamily: 'Montserrat',
            fontSize: '12px',
            color: 'rgba(0,0,0,0.6)',
            letterSpacing: '0.4px',
            lineHeight: '1.66'
          },
          '& .MuiTablePagination-selectLabel': {
            fontFamily: 'Montserrat',
            fontSize: '12px',
            color: 'rgba(0,0,0,0.6)',
            letterSpacing: '0.4px',
            lineHeight: '1.66'
          },
          '& .MuiTablePagination-displayedRows': {
            fontFamily: 'Montserrat',
            fontSize: '12px',
            color: 'rgba(0,0,0,0.87)',
            letterSpacing: '0.4px',
            lineHeight: '1.66'
          },
          '& .MuiTablePagination-select': {
            fontFamily: 'Roboto',
            fontSize: '12px',
            color: 'rgba(0,0,0,0.87)',
            letterSpacing: '0.3px',
            lineHeight: '19px'
          }
        }}
      />

      {/* Add RFID Card Modal */}
      <AddRfidCardModal
        open={isModalOpen}
        onClose={handleCloseModal}
        onSubmit={handleAddRfidCard}
        isLoading={isSubmitting}
        orgId={orgId || ''}
        unitId={unitId || ''}
      />
    </Box>
  );
};

export default ChargerAccessControl;
